// ─── AccessTags.js ────────────────────────────────────────────────────────────
// Tap-to-toggle access, safety & hazard tags for PM assessment.
// Categories based on Higgins WRA-HC016 risk assessment template.
// "Other" items are additive — each tap creates a new renamable entry.

import React, { useState } from 'react';

const ACCESS_CATEGORIES = [
  // ── Internal only ───────────────────────────────────────────────────────
  {
    label: 'Internal Access',
    areaType: 'internal',
    otherBase: 'other_int_access',
    options: [
      { id: 'high_ceilings',    label: 'High / Vaulted Ceilings', icon: '⬆️' },
      { id: 'stairwell',        label: 'Stairwell Access',        icon: '🪜' },
      { id: 'tight_spaces',     label: 'Tight / Restricted Space', icon: '🚪' },
      { id: 'ladders_int',      label: 'Ladders Required',        icon: '🪜' },
      { id: 'scaffold_int',     label: 'Internal Scaffold',       icon: '🪜' },
      { id: 'scissor_lift_int', label: 'Scissor Lift',            icon: '🏗️' },
      { id: 'boom_lift_int',    label: 'Boom Lift',               icon: '🏗️' },
    ],
  },
  {
    label: 'Internal Conditions',
    areaType: 'internal',
    otherBase: 'other_int_cond',
    options: [
      { id: 'occupied',      label: 'Occupied Space',          icon: '👥' },
      { id: 'floor_protect', label: 'Floor Protection',        icon: '🛡️' },
      { id: 'furniture',     label: 'Furniture Move / Protect', icon: '🪑' },
      { id: 'ventilation',   label: 'Ventilation Required',    icon: '💨' },
      { id: 'drying_time',   label: 'Drying Time Constraints', icon: '⏱' },
      { id: 'service_area',  label: 'Service / Plant Area',    icon: '⚙️' },
    ],
  },
  // ── External only ──────────────────────────────────────────────────────
  {
    label: 'External Access & Height',
    areaType: 'external',
    otherBase: 'other_ext_access',
    options: [
      { id: 'ewp',           label: 'EWP',              icon: '🏗️' },
      { id: 'scissor_lift',  label: 'Scissor Lift',     icon: '🏗️' },
      { id: 'boom_lift',     label: 'Boom Lift',        icon: '🏗️' },
      { id: 'abseil',        label: 'Abseil / Rope',    icon: '🧗' },
      { id: 'scaffold',      label: 'Scaffold',         icon: '🪜' },
      { id: 'swing_stage',   label: 'Swing Stage',      icon: '🔧' },
      { id: 'ladders',       label: 'Ladders',          icon: '🪜' },
      { id: 'roof',          label: 'Roof Access',      icon: '🏠' },
      { id: 'height',        label: 'Working at Height', icon: '⬆️' },
    ],
  },
  // ── Shared — both internal and external ────────────────────────────────
  {
    label: 'Site Hazards',
    otherBase: 'other_hazard',
    options: [
      { id: 'asbestos',   label: 'Asbestos',         icon: '☣️' },
      { id: 'lead_paint', label: 'Lead Paint',       icon: '⚠️' },
      { id: 'confined',   label: 'Confined Space',   icon: '🚪' },
      { id: 'exclusion',  label: 'Exclusion Zone',   icon: '🚧' },
      { id: 'electrical', label: 'Electrical',       icon: '⚡' },
      { id: 'overhead',   label: 'Overhead Hazards', icon: '⬆️' },
      { id: 'uneven',     label: 'Uneven Surface',   icon: '⚠️' },
    ],
  },
  {
    label: 'Building & Condition',
    otherBase: 'other_building',
    options: [
      { id: 'rust_rot',   label: 'Rust / Wood Rot',  icon: '🪵' },
      { id: 'building_repairs', label: 'Building Repairs Needed', icon: '🔨' },
      { id: 'prior_damage', label: 'Prior Damage',   icon: '📋' },
      { id: 'enamel',     label: 'Enamel Paint',     icon: '🎨' },
      { id: 'spraying',   label: 'Spraying Required', icon: '💨' },
      { id: 'demolition', label: 'Demo Works',       icon: '🏚️' },
    ],
  },
  {
    label: 'Site Conditions',
    otherBase: 'other_site',
    options: [
      { id: 'public_access', label: 'Public Access',  icon: '👥' },
      { id: 'multi_contractor', label: 'Multi Contractor', icon: '👷' },
      { id: 'traffic',    label: 'Traffic / Vehicles', icon: '🚗' },
      { id: 'night_work', label: 'Night Work',       icon: '🌙' },
      { id: 'remote',     label: 'Remote Location',  icon: '📍' },
      { id: 'permits',    label: 'Permits / Amenities', icon: '📝' },
      { id: 'noise',      label: 'Noise / Vibration', icon: '🔊' },
    ],
  },
];

// Flat list for lookup by id — standard (non-other) options only
const ALL_OPTIONS = ACCESS_CATEGORIES.flatMap(c => c.options);

// Check if an ID is a dynamic "other" entry
const isOther = (id) => id && id.startsWith('other_');

// Find which category a dynamic "other" ID belongs to
const findOtherCategory = (id) => {
  if (!isOther(id)) return null;
  return ACCESS_CATEGORIES.find(c => c.otherBase && id.startsWith(c.otherBase));
};

export default function AccessTags({ selected, onChange, areaType, otherLabels, onOtherLabelChange }) {
  const sel = Array.isArray(selected) ? selected : [];
  const labels = otherLabels || {};
  const [expandedCat, setExpandedCat] = useState(null);

  const toggle = (id) => {
    onChange(sel.includes(id) ? sel.filter(s => s !== id) : [...sel, id]);
  };

  const remove = (id) => {
    onChange(sel.filter(s => s !== id));
    if (isOther(id) && onOtherLabelChange) onOtherLabelChange(id, '');
  };

  const addOther = (base) => {
    const newId = `${base}_${Date.now()}`;
    onChange([...sel, newId]);
  };

  const displayLabel = (id) => {
    if (isOther(id) && labels[id]) return labels[id];
    const opt = ALL_OPTIONS.find(o => o.id === id);
    if (opt) return opt.label;
    const cat = findOtherCategory(id);
    return cat ? `Other (${cat.label})` : 'Other';
  };

  const displayIcon = (id) => {
    if (isOther(id)) return '📌';
    const opt = ALL_OPTIONS.find(o => o.id === id);
    return opt ? opt.icon : '•';
  };

  // Filter categories by area type — shared categories (no areaType) always show
  const filteredCategories = ACCESS_CATEGORIES.filter(c =>
    !c.areaType || c.areaType === areaType
  );

  // Count selected per category — includes dynamic "other" entries
  const catCounts = filteredCategories.map(cat => {
    const stdCount = cat.options.filter(o => sel.includes(o.id)).length;
    const otherCount = cat.otherBase ? sel.filter(id => id.startsWith(cat.otherBase)).length : 0;
    return { ...cat, count: stdCount + otherCount };
  });

  return (
    <div className="at-wrap">
      {sel.length > 0 && (
        <div className="at-selected">
          {sel.map(id => (
            <button key={id} className="at-tag at-active" onClick={() => remove(id)}>
              <span className="at-icon">{displayIcon(id)}</span> {displayLabel(id)} ✕
            </button>
          ))}
        </div>
      )}
      {catCounts.map(cat => (
        <div key={cat.label} className="at-category">
          <button className="at-cat-header" onClick={() => setExpandedCat(e => e === cat.label ? null : cat.label)}>
            <span>{cat.label}</span>
            {cat.count > 0 && <span className="at-cat-count">{cat.count}</span>}
            <span className="at-cat-arrow">{expandedCat === cat.label ? '▾' : '▸'}</span>
          </button>
          {expandedCat === cat.label && (
            <div className="at-cat-body">
              {cat.options.map(opt => (
                <button key={opt.id}
                  className={`at-tag ${sel.includes(opt.id) ? 'at-active' : ''}`}
                  onClick={() => toggle(opt.id)}>
                  <span className="at-icon">{opt.icon}</span> {opt.label}
                </button>
              ))}
              {cat.otherBase && sel.filter(id => id.startsWith(cat.otherBase)).map(id => (
                <div key={id} className="at-other-row">
                  <span className="at-other-icon">📌</span>
                  <input className="at-other-input" placeholder="Describe this item..."
                    value={labels[id] || ''}
                    onChange={e => onOtherLabelChange?.(id, e.target.value)}
                    autoFocus={!labels[id]} />
                  <button className="at-other-remove" onClick={() => remove(id)} aria-label="Remove tag">✕</button>
                </div>
              ))}
              {cat.otherBase && (
                <button className="at-tag at-tag-add-other" onClick={() => addOther(cat.otherBase)}>
                  ＋ Other…
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export { ALL_OPTIONS as ACCESS_OPTIONS, ACCESS_CATEGORIES, isOther, findOtherCategory };
